import React, { type ReactNode } from "react";
import { Text, Card, Button } from "@jsq/ds/core";
import { AccountActivationCardHeader } from "apps/portal/Portal/common/AccountActivation/AccountActivationCardHeader";
import classnames from "classnames";
import commonStyles from "../styles.scss";
import loginCardStyles from "./styles.scss";
import { SingleSignOnCard } from "../SingleSignOnCard";
import { type SingleSignOnCardProps } from "../SingleSignOnCard/types";
import { Divider } from "./divider";
import { providersMap } from "../SingleSignOnCard/constants";

export type LoginCardProps = {
  /**
   * Whether or not to show the "power by" header.
   */
  showPoweredByHeader?: boolean;

  /**
   * The help message shown in the "powered by" header on hover of the help icon in a popover.
   */
  poweredByHelpMessage?: string;

  /**
   * Alternate text instead of "powered by".
   */
  poweredByTitle?: string;

  /**
   * Whether or not to show the `h2` card title. Also conditional on presence of `cardTitle`.
   */
  showCardTitle?: boolean;

  /**
   * The text for the `h2` card title.
   */
  cardTitle?: string;

  /**
   * Test Id for the "powered by" header.
   */
  poweredByDataTestId?: string;

  /**
   * Test Id for the card title.
   */
  titleDataTestId?: string;

  /**
   * Description text for the component.
   */
  description?: ReactNode;

  /**
   * Test Id for the description.
   */
  descriptionDataTestId?: string;

  /**
   * Props passed to the single sign on component
   */
  ssoProps?: SingleSignOnCardProps;

  /**
   * Text for a submit button, also controls whether or not to show "submit" button. Typically used for "continue" actions to continue on to next step.
   * Optional for backwards compatibility.
   */
  submitButtonText?: string;

  /**
   * A potential custom color for the submit button background.
   */
  submitButtonBackgroundColor?: string;

  /**
   * A potential custom color for the submit button text.
   */
  submitButtonTextColor?: string;

  /**
   * Test Id for Submit Button
   */
  submitButtonTestId?: string;

  /**
   * Whether or not to disable the submit button.
   */
  disableSubmitButton?: boolean;

  /**
   * Callback which is called when the submit button is clicked.
   */
  onSubmitClick?: () => void;

  /**
   * Text for a go back button, also controls whether or not to show "go back" button. Typically used for "go back" actions to go back to previous step.
   * Optional for backwards compatibility.
   */
  goBackButtonText?: string;

  /**
   * Test Id for go back button.
   */
  goBackButtonTestId?: string;

  /**
   * Callback which is called when the go back button is clicked.
   */
  onGoBackClick?: () => void;

  /**
   * Classname for the card wrapper
   */
  className?: string;
};

/**
 * Component to display contextually styled login cards.
 *
 * Note: this component could be refactored to perform a query to directly get arena details instead of all the prop drilling that currently occurs.
 */
export const LoginCard: React.FC<LoginCardProps> = ({
  showPoweredByHeader,
  poweredByHelpMessage,
  poweredByTitle,
  showCardTitle,
  cardTitle,
  poweredByDataTestId,
  titleDataTestId,
  description,
  descriptionDataTestId,
  ssoProps,
  submitButtonText,
  submitButtonBackgroundColor,
  submitButtonTextColor,
  submitButtonTestId,
  disableSubmitButton,
  onSubmitClick,
  goBackButtonText,
  goBackButtonTestId,
  onGoBackClick,
  children,
  className = "",
}) => (
  <Card className={classnames(commonStyles.Formcard__main, className)}>
    {ssoProps && providersMap[ssoProps.provider] && (
      <>
        <SingleSignOnCard
          isContained
          provider={ssoProps.provider}
          providerName={ssoProps.providerName}
          arena={ssoProps.arena}
          onSignIn={ssoProps.onSignIn}
        />
        <Divider>OR</Divider>
      </>
    )}
    <div className={loginCardStyles.Login__wrapper}>
      {showPoweredByHeader && (
        <div data-testid={poweredByDataTestId} data-cy={poweredByDataTestId}>
          <AccountActivationCardHeader
            message={poweredByTitle}
            helpMessage={poweredByHelpMessage}
          />
        </div>
      )}

      {showCardTitle && cardTitle && (
        <Text
          className={loginCardStyles.Login__cardCenter}
          align="center"
          variant="h2"
          data-testid={titleDataTestId}
          data-cy={titleDataTestId}
          marginBottom="sm"
        >
          {cardTitle}
        </Text>
      )}

      {description && (
        <Text
          className={loginCardStyles.Login__cardCenter}
          variant="secondary-md"
          align="center"
          data-testid={descriptionDataTestId}
          data-cy={descriptionDataTestId}
        >
          {description}
        </Text>
      )}

      {children}

      {submitButtonText && onSubmitClick && (
        <Button
          type="submit"
          isFullWidth
          className={classnames("inline-sm", loginCardStyles.Login__submitButton, {
            "stacked-md": !!goBackButtonText && !!onGoBackClick,
          })}
          disabled={disableSubmitButton}
          variant={submitButtonBackgroundColor || submitButtonTextColor ? "custom" : "primary"}
          backgroundColor={submitButtonBackgroundColor}
          textColor={submitButtonTextColor}
          onClick={onSubmitClick}
          data-testid={submitButtonTestId}
          data-cy={submitButtonTestId}
        >
          {submitButtonText}
        </Button>
      )}

      {goBackButtonText && onGoBackClick && (
        <Button
          variant="link"
          isFullWidth
          onClick={onGoBackClick}
          data-testid={goBackButtonTestId}
          data-cy={goBackButtonTestId}
        >
          {goBackButtonText}
        </Button>
      )}
    </div>
  </Card>
);
